<script setup lang="ts">
import { Download } from "@element-plus/icons-vue";
import { ref, watch } from "vue";
import { FormType, LinkedWorkflowSummaryDto } from "~/api";
import {
  compareFormVersions,
  getFormVersionKey,
} from "~/NewFormBuilder/composables/useFormVersions";
import { usePdfLoadingStore } from "~/stores/pdfLoadingStore";
import { apiClient } from "~/utils/apiClient";
import { parseDate } from "~/utils/dateUtils";
import { showError } from "~/utils/handleAPIError";
import { hubManager } from "~/utils/hubManager";
import { splitByCaps } from "~/utils/miscUtils";
const pdfLoadingStore = usePdfLoadingStore();

interface FormRow {
  formResponseGuid: string;
  formName: string;
  status: string;
}

interface WorkflowRow {
  workflowGuid: string;
  workflowName: string;
  workflowDefinitionName: string;
  workflowStatus: string;
  workflowArchivedAt?: string | null;
  linkedAt?: string | null;
  primaryEntityName?: string | null;
  linkType?: string | null;
  forms: FormRow[];
  loadingForms: boolean;
}

const props = defineProps<{
  workflows: LinkedWorkflowSummaryDto[];
}>();

const emit = defineEmits<{
  (
    e: "workflowSelected",
    workflowGuid: string,
    formResponseGuid?: string,
  ): void;
}>();

const loading = ref(false);
const workflowRows = ref<WorkflowRow[]>([]);

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  const date = parseDate(dateString);
  return date ? date.toLocaleDateString() : dateString;
};

const getStatusType = (
  status: string,
): "success" | "warning" | "danger" | "info" | "primary" => {
  const statusLower = status.toLowerCase();
  if (
    statusLower.includes("complete") ||
    statusLower.includes("approved") ||
    statusLower.includes("accepted")
  ) {
    return "success";
  }
  if (statusLower.includes("pending") || statusLower.includes("review")) {
    return "warning";
  }
  if (statusLower.includes("reject") || statusLower.includes("cancel")) {
    return "danger";
  }
  return "info";
};

const loadForms = async () => {
  if (props.workflows.length === 0) {
    workflowRows.value = [];
    return;
  }

  loading.value = true;

  workflowRows.value = props.workflows.map((wf) => ({
    workflowGuid: wf.workflowGuid,
    workflowName: wf.workflowName ?? "",
    workflowDefinitionName: wf.workflowDefinitionName ?? "",
    workflowStatus: wf.workflowStatus ?? "",
    workflowArchivedAt: wf.workflowArchivedAt,
    linkedAt: wf.linkedAt,
    primaryEntityName: wf.primaryEntityName,
    linkType: wf.linkType,
    forms: [],
    loadingForms: true,
  }));

  loading.value = false;

  await Promise.all(
    workflowRows.value.map(async (row) => {
      try {
        const content = await apiClient.workflowInstance.getWorkflow(
          row.workflowGuid,
        );
        const wfData = content.workflow;

        for (const fr of wfData.formResponses ?? []) {
          const def = wfData.workflowDefinition?.formDefinitions?.find(
            (fd) => fd.guid === fr.formDefinitionGuid,
          );
          if (def) {
            fr.formDefinition = def;
            fr.formType = def.formType;
          }
        }

        // Determine which responses are superseded (an older version whose template also has a
        // newer-version response present) so only the latest version shows. Uses the same
        // compareFormVersions ordering as the version labels, and only counts groups spanning
        // ≥2 distinct definitions (a real upgrade) — single-version forms are never hidden.
        const supersededGuids = new Set<string>();
        const byTemplate = new Map<
          string,
          NonNullable<typeof wfData.formResponses>
        >();
        for (const fr of wfData.formResponses ?? []) {
          if (!fr.formDefinition) continue;
          const key = getFormVersionKey(fr.formDefinition);
          const list = byTemplate.get(key);
          if (list) list.push(fr);
          else byTemplate.set(key, [fr]);
        }
        for (const group of byTemplate.values()) {
          const distinctDefs = new Set(group.map((r) => r.formDefinitionGuid));
          if (distinctDefs.size < 2) continue;
          const sorted = [...group].sort((a, b) =>
            compareFormVersions(a.formDefinition!, b.formDefinition!),
          );
          const active = sorted[sorted.length - 1];
          for (const r of sorted) {
            if (r.guid && r.guid !== active.guid) supersededGuids.add(r.guid);
          }
        }

        row.forms = (wfData.formResponses ?? [])
          .filter((fr) => fr.formType !== FormType.CONFIGURED_FORM)
          // Hide superseded (previous-version) responses — show only the latest per template.
          .filter((fr) => !fr.guid || !supersededGuids.has(fr.guid))
          .sort(
            (a, b) =>
              (a.formDefinition?.sortOrder ?? 0) -
              (b.formDefinition?.sortOrder ?? 0),
          )
          .map((fr) => ({
            formResponseGuid: fr.guid ?? "",
            formName: fr.formDefinition?.name ?? "Unknown Form",
            status: fr.status ?? "",
          }));
      } catch (e) {
        showError(e);
        row.forms = [];
      } finally {
        row.loadingForms = false;
      }
    }),
  );
};

const handleGetPdf = async (workflowGuid: string) => {
  const pdfCode = "";
  if (!workflowGuid) {
    return;
  }
  // Ensure we pass the ConnectionId for the "self" hub (PdfMsg listener lives there).
  await hubManager.getHub("self", "/self");
  const selfConnId =
    hubManager.getHubConnectionId("self") ?? hubManager.ConnectionId ?? "";
  await pdfLoadingStore.makePdf(pdfCode, workflowGuid, selfConnId);
};

const handleWorkflowClick = (row: WorkflowRow) => {
  emit("workflowSelected", row.workflowGuid);
};

const handleFormClick = (
  workflowGuid: string,
  formResponseGuid: string,
  e: MouseEvent,
) => {
  e.stopPropagation();
  emit("workflowSelected", workflowGuid, formResponseGuid);
};

// Rebuild rows whenever the parent refreshes its linked-workflow list
// (e.g. after a form completion changes a workflow's status).
watch(() => props.workflows, loadForms, { immediate: true });
</script>

<template>
  <div class="linked-workflows-table">
    <b style="margin-right: 8px">Forms ({{ workflows.length }})</b>

    <el-table
      v-if="workflowRows.length > 0 || loading"
      v-loading="loading"
      :data="workflowRows"
      stripe
      row-key="workflowGuid"
      style="width: 100%; margin-top: 6px"
      size="small"
      class="workflows-table"
    >
      <!-- Expand toggle -->
      <el-table-column type="expand" width="32">
        <template #default="{ row }: { row: WorkflowRow }">
          <div class="forms-sublist">
            <div v-if="row.loadingForms" class="forms-loading">
              <span>Loading forms…</span>
            </div>

            <div v-else-if="row.forms.length === 0" class="forms-empty">
              No forms
            </div>

            <div
              v-for="form in row.forms"
              v-else
              :key="form.formResponseGuid"
              class="form-subrow"
              @click="
                handleFormClick(row.workflowGuid, form.formResponseGuid, $event)
              "
            >
              <div class="subcell subcell-name">
                <span class="form-bullet">↳</span>
                <el-link type="primary" class="form-link">{{
                  form.formName
                }}</el-link>
              </div>
              <div class="subcell subcell-status">
                <el-tag :type="getStatusType(form.status)" size="small">
                  {{ splitByCaps(form.status) }}
                </el-tag>
              </div>
              <div class="subcell subcell-entity" />
              <div class="subcell subcell-rel" />
              <div class="subcell subcell-linked" />
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="Name" width="400">
        <template #default="{ row }: { row: WorkflowRow }">
          <div
            class="workflow-name-cell"
            @click.stop="handleWorkflowClick(row)"
          >
            <el-link type="primary" class="workflow-name">
              {{ row.workflowName || row.workflowDefinitionName }}
            </el-link>
            <span
              v-if="
                row.workflowName &&
                row.workflowDefinitionName !== row.workflowName
              "
              class="workflow-type"
            >
              {{ row.workflowDefinitionName }}
            </span>
            <el-tag
              v-if="row.workflowArchivedAt"
              size="small"
              type="info"
              effect="plain"
            >
              Archived
            </el-tag>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="Status" width="200">
        <template #default="{ row }: { row: WorkflowRow }">
          <el-tag :type="getStatusType(row.workflowStatus)" size="small">
            {{ splitByCaps(row.workflowStatus) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Primary Entity" width="140">
        <template #default="{ row }: { row: WorkflowRow }">
          <span v-if="row.primaryEntityName" class="primary-entity">
            {{ row.primaryEntityName }}
          </span>
          <span v-else class="muted">-</span>
        </template>
      </el-table-column>

      <el-table-column label="Relationship" width="120">
        <template #default="{ row }: { row: WorkflowRow }">
          <el-tag
            v-if="row.linkType === 'Primary'"
            size="small"
            type="success"
            effect="light"
          >
            Primary
          </el-tag>
          <el-tag
            v-else-if="row.linkType"
            size="small"
            type="info"
            effect="plain"
          >
            {{ row.linkType }}
          </el-tag>
          <span v-else class="muted">Linked</span>
        </template>
      </el-table-column>

      <el-table-column label="Linked" width="100">
        <template #default="{ row }: { row: WorkflowRow }">
          {{ formatDate(row.linkedAt) }}
        </template>
      </el-table-column>

      <el-table-column label="PDF" width="80" align="center">
        <template #default="{ row }: { row: WorkflowRow }">
          <el-button
            circle
            size="small"
            :icon="Download"
            aria-label="Download PDF"
            @click.stop="handleGetPdf(row.workflowGuid)"
          />
        </template>
      </el-table-column>
    </el-table>

    <el-empty
      v-else-if="!loading"
      description="No workflows found"
      :image-size="60"
    />
  </div>
</template>

<style lang="less" scoped>
.linked-workflows-table {
  .workflows-table {
    :deep(.el-table__row) {
      cursor: pointer;
    }

    :deep(.el-table__expanded-cell) {
      padding: 0;
      background-color: var(--el-fill-color-lighter);
    }

    .workflow-name-cell {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;

      .workflow-name {
        font-weight: 500;
      }

      .workflow-type {
        font-size: 0.78rem;
        color: var(--el-text-color-secondary);
      }
    }

    .primary-entity {
      color: var(--el-text-color-regular);
    }

    .muted {
      color: var(--el-text-color-placeholder);
    }
  }

  // ── Expanded form sub-list ───────────────────────────────────────────────
  .forms-sublist {
    display: flex;
    flex-direction: column;
    border-top: 1px solid var(--el-border-color-extra-light);
  }

  .forms-loading,
  .forms-empty {
    padding: 6px 12px 6px 44px;
    font-size: 0.82rem;
    color: var(--el-text-color-secondary);
  }

  .form-subrow {
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: background-color 0.12s;

    padding-left: 80px;

    &:hover {
      background-color: var(--el-color-primary-light-9);
    }

    .subcell {
      display: flex;
      align-items: center;
      padding: 6px 12px;
      box-sizing: border-box;
      flex-shrink: 0;
    }

    .subcell-name {
      width: 350px;
      gap: 6px;
    }

    .subcell-status {
      width: 160px;
    }
    .subcell-entity {
      width: 140px;
    }
    .subcell-rel {
      width: 120px;
    }
    .subcell-linked {
      width: 100px;
    }

    .form-bullet {
      color: var(--el-text-color-placeholder);
      font-size: 0.8rem;
      flex-shrink: 0;
      user-select: none;
    }

    .form-link {
      font-size: 0.88rem;
    }
  }
}
</style>
